import { createLink } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { Image } from "@/components/Image/Image";
import type {
  PublicContentItemData,
  PublicContentBlockData,
} from "@/types/codegen";

type ContentBlockDrawerProps = {
  open: boolean;
  onClose: () => void;
  contentBlock?: PublicContentBlockData;
  onButtonClick?: (buttonText: string) => void;
};

export function ContentBlockDrawer({
  open,
  onClose,
  contentBlock,
  onButtonClick,
}: ContentBlockDrawerProps) {
  const intl = useIntl();

  const hasButtonContentItem = contentBlock?.contentItems.some(
    (item) => item.type === "button" && item.text && item.linkUrl,
  );

  return (
    <SwipeableDrawer
      open={open}
      onClose={onClose}
      hasFullHeight={contentBlock?.drawerStyle === "FULL"}
    >
      <div className="flex flex-col gap-2">
        {contentBlock?.contentItems.map((item, index) => (
          <ContentBlockItemRenderer
            key={index}
            item={item}
            onButtonClick={(...args) => {
              onButtonClick?.(...args);

              onClose();
            }}
          />
        ))}

        {!hasButtonContentItem && (
          <Button variant="contained" size="large" onClick={onClose}>
            {intl.formatMessage({
              id: "common.close",
              defaultMessage: "Close",
            })}
          </Button>
        )}
      </div>
    </SwipeableDrawer>
  );
}

const ButtonLink = createLink(Button);

type ContentBlockItemRendererProps = {
  item: PublicContentItemData;
  onButtonClick?: (buttonText: string) => void;
};

function ContentBlockItemRenderer({
  item,
  onButtonClick,
}: ContentBlockItemRendererProps) {
  switch (item.type) {
    case "image":
      if (!item.imageUrl) return null;

      return (
        <Image
          src={item.imageUrl}
          alt={item.alt ?? undefined}
          className="aspect-[21/9] w-full rounded-2xl object-cover"
        />
      );

    case "heading":
      return (
        <h1 className="text-lg font-semibold text-text-title">
          {item.content}
        </h1>
      );

    case "text":
      return <p className="text-text-primary">{item.content}</p>;

    case "bullets":
      if (!item.items || item.items.length === 0) return null;

      return (
        <ul className="list-disc ps-5 text-sm text-text-primary marker:text-xs">
          {item.items.map((bulletItem, index) => (
            <li key={index}>{bulletItem}</li>
          ))}
        </ul>
      );

    case "button": {
      const { linkUrl, text } = item;
      if (!linkUrl || !text) return null;

      return (
        <ButtonLink
          to={linkUrl}
          variant="contained"
          size="large"
          fullWidth
          onClick={() => onButtonClick?.(text)}
        >
          {text}
        </ButtonLink>
      );
    }

    default:
      return null;
  }
}
